import React, { useState } from "react";
import { Modal, Form, notification, Upload, Row, Col, Input } from "antd";
import Joi from "joi";
import { PlusOutlined } from "@ant-design/icons";
import axios from "axios"; // ✅ use axios directly
import config from "../../../../config/config";
import helper from "../../../../helpers/helper";

const Create = ({ isVisible, handleClose, moduleName, base_url }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [formError, setFormError] = useState({});

  const sizeLimit = config.sizeLimit;
  const allowedExtensions = config.allowed_extensions;
  const joiOptions = config.joiOptions;

  // ---------- VALIDATION ----------
  const validate = (value) => {
    let schemaObj = {
      bgImg: Joi.required().label("Desktop Background Image"),
      mbBgImg: Joi.required().label("Mobile Background Image"),
      initial: Joi.string().required().label("Initial"),
      icon: Joi.required().label("Icon"),
      title: Joi.string().required().label("Title"),
      desc: Joi.string().required().label("Description"),
    };

    const schema = Joi.object(schemaObj).options({ allowUnknown: true });
    const { error } = schema.validate(value, joiOptions);

    const errors = {};
    if (error) {
      error.details.forEach((field) => {
        errors[field.path[0]] = field.message;
      });
    }

    // Validate images
    helper.validateSize("bgImg", value.bgImg, sizeLimit, errors);
    helper.validateExt("bgImg", value.bgImg, allowedExtensions, errors);
    helper.validateSize("mbBgImg", value.mbBgImg, sizeLimit, errors);
    helper.validateExt("mbBgImg", value.mbBgImg, allowedExtensions, errors);
    helper.validateSize("icon", value.icon, sizeLimit, errors);
    helper.validateExt("icon", value.icon, allowedExtensions, errors);

    return errors;
  };

  // ---------- SUBMIT ----------
  const handleSubmit = async (value) => {
    const errors = validate(value);
    setFormError(errors);
    if (Object.keys(errors).length) return;

    setLoading(true);
    const formData = new FormData();

    formData.append("initial", value.initial);
    formData.append("title", value.title);
    formData.append("desc", value.desc);

    if (value.bgImg) formData.append("bgImgWeb", value.bgImg.file);
    if (value.mbBgImg) formData.append("bgImgMobile", value.mbBgImg.file);
    if (value.icon) formData.append("icon", value.icon.file);

    try {
      const response = await axios.post(`${base_url}addCareerValue`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (response?.data?.message === "Career value added successfully") {
        notification.success({
          message: `${moduleName} Added Successfully`,
          placement: "topRight",
        });

        form.resetFields();
        handleClose(true); // Pass true to refresh list in parent
      } else {
        notification.error({
          message: response?.data?.message || "Failed to add record",
          placement: "topRight",
        });
      }
    } catch (error) {
      notification.error({
        message: error?.response?.data?.message || "Something went wrong",
        placement: "topRight",
      });
    }

    setLoading(false);
  };

  const uploadProps = {
    beforeUpload: (file) => false,
    maxCount: 1,
    listType: "picture-card",
    showUploadList: { showPreviewIcon: false, showRemoveIcon: false },
  };

  return (
    <Modal
      title={`Add ${moduleName}`}
      visible={isVisible}
      onCancel={() => handleClose(false)}
      okText="Create"
      cancelText="Cancel"
      confirmLoading={loading}
      onOk={() => {
        form
          .validateFields()
          .then((values) => handleSubmit(values))
          .catch(() => {});
      }}
      width={700}
    >
      <Form layout="vertical" form={form}>
        <Row gutter={16}>
          <Col xs={24} md={8}>
            <Form.Item
              required
              label="Desktop Background Image"
              extra="Recommended size: 310 × 369"
              name="bgImg"
              {...(formError.bgImg && {
                help: formError.bgImg,
                validateStatus: "error",
              })}
            >
              <Upload {...uploadProps}>
                <PlusOutlined />
              </Upload>
            </Form.Item>
          </Col>

          <Col xs={24} md={8}>
            <Form.Item
              required
              label="Mobile Background Image"
              extra="Recommended size: 335 × 218"
              name="mbBgImg"
              {...(formError.mbBgImg && {
                help: formError.mbBgImg,
                validateStatus: "error",
              })}
            >
              <Upload {...uploadProps}>
                <PlusOutlined />
              </Upload>
            </Form.Item>
          </Col>

          <Col xs={24} md={8}>
            <Form.Item
              required
              label="Icon"
              extra="Recommended size: 48 × 46"
              name="icon"
              {...(formError.icon && {
                help: formError.icon,
                validateStatus: "error",
              })}
            >
              <Upload {...uploadProps}>
                <PlusOutlined />
              </Upload>
            </Form.Item>
          </Col>

          <Col xs={24} md={12}>
            <Form.Item
              required
              label="Initial"
              name="initial"
              {...(formError.initial && {
                help: formError.initial,
                validateStatus: "error",
              })}
            >
              <Input maxLength={1} />
            </Form.Item>
          </Col>

          <Col xs={24} md={12}>
            <Form.Item
              required
              label="Title"
              name="title"
              extra="Recommended character limit: 18"
              {...(formError.title && {
                help: formError.title,
                validateStatus: "error",
              })}
            >
              <Input />
            </Form.Item>
          </Col>

          <Col xs={24}>
            <Form.Item
              required
              label="Description"
              name="desc"
              extra="Recommended character limit: 150"
              {...(formError.desc && {
                help: formError.desc,
                validateStatus: "error",
              })}
            >
              <Input.TextArea rows={3} />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default Create;
